/**
 * =========================================================================================
 * Billing Payments - Subscription Header Component
 * =========================================================================================
 */

import React from 'react';
import moment from 'moment';
import UTags from 'ubs-framework/v2/UTags';
import UPopup from 'ubs-framework/v2/UPopup'
import { getKeyName, numberWithComaToFixed } from 'ubs-framework/v2/common/function';
import * as Icon from 'ubs-icons';
import { BILLING_CYCLE_OPTIONS, SUBSCRIPTION_STATUS, SUBSCRIPTION_STATUS_OPTIONS } from '../../../../../common/constant';
import UIcon from 'ubs-framework/v2/UIcon';
import { COLOR } from 'ubs-framework/v2/common/constant';
import { dateFormatter } from 'ubs-framework/v2/common/function';
import { useSelector } from 'react-redux';

const SubscriptionHeader = React.memo(({ currentSubscription, summary, onPause, onResume, onCancel, onCreateNew, onViewTimeline, actionLoading }) => {
    const _PI = useSelector(e => e?.common?.planInfo);

    if (!currentSubscription && !summary) return null;

    const getBillingCycleLabel = (cycle) => {
        return getKeyName(BILLING_CYCLE_OPTIONS, cycle)?.label || cycle || '-';
    };

    const statusOption = getKeyName(SUBSCRIPTION_STATUS_OPTIONS, currentSubscription?.status);
    const subStatus = currentSubscription?.status;
    const canCancel = [SUBSCRIPTION_STATUS.INITIATED, SUBSCRIPTION_STATUS.INITIALIZED, SUBSCRIPTION_STATUS.ACTIVE, SUBSCRIPTION_STATUS.PAUSED, SUBSCRIPTION_STATUS.ON_HOLD, SUBSCRIPTION_STATUS.CUSTOMER_PAUSED].includes(subStatus);
    const hasActions = (canCancel || subStatus === SUBSCRIPTION_STATUS.CANCELLED || subStatus === SUBSCRIPTION_STATUS.CUSTOMER_CANCELLED) && currentSubscription?.subscriptionId;

    const infoItems = [
        { label: 'Billing Cycle', value: getBillingCycleLabel(currentSubscription?.billingCycle) },
        { label: 'Seats', value: currentSubscription?.seatCount ?? '-' },
        { label: 'Next Payment (Auto pay)', value: dateFormatter({ date: currentSubscription?.nextPaymentDate, format: 'DD MMM YYYY' }) },
        { label: 'Total Amount', value: numberWithComaToFixed(currentSubscription?.totalAmount, currentSubscription?.currency), bold: true },
    ];

    const summaryItems = [
        { label: 'Total Payments', value: summary?.totalPayments || 0, icon: Icon.CreditCard, colorCode: COLOR?.u_icon_bg },
        { label: 'Successful', value: summary?.successCount || 0,  icon: Icon.CheckMarkRound, colorCode: COLOR?.u_green },
        { label: 'Failed', value: summary?.failedCount || 0,   icon: Icon.CloseMarkRound, colorCode: COLOR?.u_red },
        { label: 'Cancelled', value: summary?.cancelledCount || 0, icon: Icon.CloseMarkRound, colorCode: COLOR?.u_font },
        { label: 'Pending', value: summary?.pendingCount || 0,  icon: Icon.Clock, colorCode: COLOR?.u_orange },
        { label: 'Total Paid', value: summary?.totalPaidAmount != null ? numberWithComaToFixed(summary.totalPaidAmount, currentSubscription?.currency) : '-', icon: Icon.CreditCard, colorCode: COLOR?.u_blue },
    ];

    return (
        <div>
            {currentSubscription && (
                <div className="u_radius-8 u_pt-20 u_px-20 u_mb-20 u_b-light">

                    <div className="u_flex-align u_content-between u_mb-16">
                        <div className="u_flex-align">
                            <div className="u_bg-white u_radius-8 u_p-10 u_b-light u_mr-14">
                                <Icon.CreditCard size={22} fill={COLOR?.u_blue} />
                            </div>
                            <div>
                                <p className="u_small-font u_color-font u_mb-4">Current Subscription</p>
                                <p className="u_font-16 u_fw-700 u_color-title">
                                    {currentSubscription?.planCode || currentSubscription?.planKey || '-'}
                                </p>
                            </div>
                        </div>

                        <div className="u_flex-align">
                            {currentSubscription?.subscriptionId && onViewTimeline && (
                                <div
                                    className="u_flex-align u_cp u_small-font u_fw-500 u_color-blue u_mr-10"
                                    onClick={() => onViewTimeline(currentSubscription)}
                                >
                                    <Icon.Clock size={16} fill={COLOR?.u_blue} className="u_mr-4" />
                                    <span>View Timeline</span>
                                </div>
                            )}
                            <UTags
                                className="u_ib"
                                title={statusOption?.label || '-'}
                                color={statusOption?.color || '#999999'}
                            />
                            {hasActions && (
                                <UPopup
                                    params={{ position: "bottom right" }}
                                    desc={
                                        <ul className="u_menu_list">
                                            {subStatus === SUBSCRIPTION_STATUS.ACTIVE && (
                                                <li onClick={onPause} style={{ alignItems: 'center' }}>
                                                    <Icon.Clock fill={COLOR?.u_orange} size={18} className="u_mr-10" />
                                                    {actionLoading === 'pause' ? 'Pausing...' : 'Pause Subscription'}
                                                </li>
                                            )}
                                            {[SUBSCRIPTION_STATUS.PAUSED, SUBSCRIPTION_STATUS.CUSTOMER_PAUSED]?.includes(subStatus) && (
                                                <li onClick={onResume} style={{ alignItems: 'center' }}>
                                                    <Icon.CheckMarkRound fill={COLOR?.u_green} size={18} className="u_mr-10" />
                                                    {actionLoading === 'resume' ? 'Reactivating...' : 'Reactivate'}
                                                </li>
                                            )}
                                            {canCancel && (
                                                <li onClick={onCancel} style={{ alignItems: 'center' }}>
                                                    <Icon.CloseMarkRound fill={COLOR?.u_red} size={18} className="u_mr-10" />
                                                    {actionLoading === 'cancel' ? 'Cancelling...' : 'Cancel Subscription'}
                                                </li>
                                            )}
                                            {[SUBSCRIPTION_STATUS.CANCELLED, SUBSCRIPTION_STATUS.CUSTOMER_CANCELLED]?.includes(subStatus) && (
                                                <li onClick={onCreateNew} style={{ alignItems: 'center' }}>
                                                    <Icon.CreditCard fill={COLOR?.u_blue} size={18} className="u_mr-10" />
                                                    Create New Subscription
                                                </li>
                                            )}
                                        </ul>
                                    }
                                >
                                    <span className="u_cp u_ml-10">
                                        <Icon.HorizontalMenu size={20} fill={COLOR.u_icon_bg} />
                                    </span>
                                </UPopup>
                            )}
                        </div>
                    </div>

                    {/* Info chips */}
                    <div className="u_row">
                        {infoItems?.map(item => (
                            <div key={item.label} className="u_col-3 u_mb-20">
                                <div className="u_bg-thead u_radius-8 u_px-14 u_py-12 u_b-light">
                                    <p className="u_small-font u_color-font u_mb-6">{item.label}</p>
                                    <p className={`u_small-font u_color-title ${item.bold ? 'u_fw-700' : 'u_fw-600'}`}>
                                        {item.value}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {summary && (
                <div className="u_row">
                    {summaryItems?.map(item => (
                        <div key={item.label} className="u_col-2 u_mb-16">
                            <div className="u_bg-white u_b-light u_radius-8 u_px-16 u_py-16">
                                <div className="u_flex-align">
                                    <UIcon
                                        color={item.colorCode}
                                        thumb={48}
                                        icon={<item.icon size={20} fill={item.colorCode} />}
                                        type="square"
                                    />
                                    <div className="u_pl-12">
                                        <h5 className="u_semi-large-font u_fw-700 u_color-title">{item.value}</h5>
                                        <p className="u_small-font u_color-font u_mt-4">{item.label}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

export default SubscriptionHeader;
